/**
 * Semanas retail como TEXTO `AAAA-MM-DD` con aritmética en UTC — espejo de
 * `cdr-sa-api/src/modules/metas-colaborador/domain/semana.ts`.
 *
 * La semana retail va de lunes a domingo y su número coincide con el ISO. Aquí
 * NUNCA se hace `new Date('AAAA-MM-DD')` para una fecha suelta: el navegador la
 * interpreta en UTC y la pinta en hora local, el día ANTERIOR en México. Y
 * ordenar fechas como texto de `Date` ya acreditó 37 créditos al vendedor
 * equivocado. Todo pasa por `Date.parse` con la zona explícita y se lee con
 * los métodos `getUTC*`.
 *
 * La única fecha LOCAL es la del calendario del administrador, y se lee y se
 * escribe campo por campo (`isoDeCalendario` / `calendarioDeIso`).
 */
const DIA = 86_400_000;

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export const ms = (iso: string): number => Date.parse(`${iso}T00:00:00Z`);
export const aIso = (t: number): string => new Date(t).toISOString().slice(0, 10);

/** `true` sólo para una fecha real con el formato exacto (rechaza 2026-02-30). */
export function esFechaIso(s: unknown): s is string {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const t = ms(s);
  return Number.isFinite(t) && aIso(t) === s;
}

/** 1 = lunes … 7 = domingo. */
export const diaSemana = (iso: string): number => ((new Date(ms(iso)).getUTCDay() + 6) % 7) + 1;

export const sumarDias = (iso: string, n: number): string => aIso(ms(iso) + n * DIA);

/** Lunes de la semana retail que contiene `iso`. */
export const lunesDe = (iso: string): string => sumarDias(iso, -(diaSemana(iso) - 1));

export const esLunes = (iso: string): boolean => esFechaIso(iso) && diaSemana(iso) === 1;

export const domingoDe = (lunes: string): string => sumarDias(lunes, 6);

/** Número de semana ISO (= semana retail). La 53 existe: 2026 tiene 53. */
export function numeroSemana(iso: string): number {
  const jueves = sumarDias(iso, 4 - diaSemana(iso));
  const primero = ms(`${jueves.slice(0, 4)}-01-01`);
  return Math.floor((ms(jueves) - primero) / DIA / 7) + 1;
}

const partes = (iso: string) => ({
  anio: iso.slice(0, 4),
  mes: MESES[Number(iso.slice(5, 7)) - 1] ?? '',
  dia: String(Number(iso.slice(8, 10))),
});

/**
 * El día en que el regional autoriza la semana: el VIERNES anterior a su lunes
 * (decisión del usuario, 21-oct-2026: miércoles el gerente, jueves el supervisor,
 * viernes el regional). Sólo recuerda: nada se bloquea por día. `2026-11-02` →
 * `{ fecha: '2026-10-30', texto: 'viernes 30-oct' }` (diseño `Regional-filtro`).
 */
export function limiteRegional(lunes: string): { fecha: string; texto: string } {
  const fecha = sumarDias(lunesDe(lunes), -3);
  const p = partes(fecha);
  return { fecha, texto: `viernes ${p.dia}-${p.mes}` };
}

/** `2026-09-21` → «21 sep 2026». Para fechas sueltas (alta en BUK, lunes). */
export function fechaCorta(iso: string | null | undefined): string {
  if (!esFechaIso(iso)) return '—';
  const p = partes(iso);
  return `${p.dia} ${p.mes} ${p.anio}`;
}

/**
 * «Semana 39 · 21 al 27 sep». Si la semana cruza de mes, los dos meses
 * («Semana 40 · 28 sep al 4 oct»); si cruza de año, también el año, porque la
 * semana 53 de 2026 termina en 2027 y «28 dic al 3 ene» se lee como un error.
 */
export function etiquetaSemana(lunes: string, conAnio = false): string {
  if (!esFechaIso(lunes)) return lunes;
  const l = lunesDe(lunes);
  const d = domingoDe(l);
  const a = partes(l);
  const b = partes(d);
  const n = numeroSemana(l);
  let rango: string;
  if (a.anio !== b.anio) rango = `${a.dia} ${a.mes} ${a.anio} al ${b.dia} ${b.mes} ${b.anio}`;
  else if (a.mes !== b.mes) rango = `${a.dia} ${a.mes} al ${b.dia} ${b.mes}`;
  else rango = `${a.dia} al ${b.dia} ${b.mes}`;
  const anio = conAnio && a.anio === b.anio ? ` ${b.anio}` : '';
  return `Semana ${n} · ${rango}${anio}`;
}

/** La misma etiqueta dentro de una frase: «desde la semana 40 · 28 sep al 4 oct». */
export const semanaEnFrase = (lunes: string, conAnio = false): string =>
  etiquetaSemana(lunes, conAnio).replace(/^Semana/, 'semana');

/**
 * El día que el usuario eligió en un calendario, como texto.
 *
 * El `Date` de un calendario representa medianoche LOCAL del día elegido, así
 * que se leen sus campos locales (no `toISOString`, que en México a partir de
 * las 18:00 lo pasaría al día siguiente).
 */
export function isoDeCalendario(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Lo contrario, para darle al calendario un valor inicial. Sólo para el componente. */
export function calendarioDeIso(iso: string): Date {
  return new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
}

/**
 * Las semanas del selector: las que se capturan (`ventanaCaptura`, o `ventana`
 * de una API anterior) más las que se resuelven (`ventanaResolucion`: la recién
 * cerrada hasta el martes). Sin la segunda, el regional no llegaba a la semana
 * que todavía puede autorizar o rechazar.
 */
export function semanasDelSelector(a: {
  ventana: string[];
  ventanaCaptura?: string[];
  ventanaResolucion?: string[];
}): string[] {
  const todas = new Set([...(a.ventanaCaptura ?? a.ventana ?? []), ...(a.ventanaResolucion ?? [])]);
  return Array.from(todas).sort((x, y) => ms(x) - ms(y));
}

/**
 * La semana como la escribe el diseño: número aparte (va en negritas) y el
 * rango con guion largo — «Semana 40 · 28 sep – 4 oct 2026».
 *
 *   rango:      «28 sep – 4 oct 2026» · «21 – 27 sep 2026» · «28 dic 2026 – 3 ene 2027»
 *   rangoCorto: lo mismo sin el año, para el teléfono («28 sep – 4 oct»); si
 *               la semana cruza de año lo conserva, por la misma razón que
 *               `etiquetaSemana`.
 */
export function partesSemana(lunes: string): { numero: number; rango: string; rangoCorto: string } {
  const l = lunesDe(lunes);
  const d = domingoDe(l);
  const a = partes(l);
  const b = partes(d);
  const numero = numeroSemana(l);
  if (a.anio !== b.anio) {
    const r = `${a.dia} ${a.mes} ${a.anio} – ${b.dia} ${b.mes} ${b.anio}`;
    return { numero, rango: r, rangoCorto: r };
  }
  const sinAnio = a.mes !== b.mes ? `${a.dia} ${a.mes} – ${b.dia} ${b.mes}` : `${a.dia} – ${b.dia} ${b.mes}`;
  return { numero, rango: `${sinAnio} ${b.anio}`, rangoCorto: sinAnio };
}

/** «Semana 40 · 28 sep – 4 oct 2026» en una sola cadena (opciones, avisos). */
export const etiquetaSemanaDiseno = (lunes: string): string => {
  if (!esFechaIso(lunes)) return lunes;
  const p = partesSemana(lunes);
  return `Semana ${p.numero} · ${p.rango}`;
};
